import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, ArrowLeft, Search, Package, Trash2, Save, Send, RefreshCw, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { stockRequestsAPI, productsAPI } from '@/services/api';
import HighlightText from '@/components/HighlightText';

const StockRequestCreatePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const warehouseId = user?.entity_id;
  const warehouseName = user?.entity_name || 'Warehouse';
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [notes, setNotes] = useState('');
  const [requiredByDate, setRequiredByDate] = useState('');
  const [items, setItems] = useState([]);
  
  // Product search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchReqRef = useRef(0);
  const [searchLoading, setSearchLoading] = useState(false);

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    const reqId = ++searchReqRef.current;
    try {
      const response = await productsAPI.search(query.trim(), 20);
      if (reqId !== searchReqRef.current) return;
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchProducts]);

  // Add product to request
  const addProduct = (product) => {
    if (items.some(i => i.product_id === product.id)) {
      toast({ title: 'Already Added', description: 'This product is already in the request', variant: 'destructive' });
      return;
    }
    
    setItems(prev => [...prev, {
      product_id: product.id,
      product_sku: product.sku,
      product_name: product.name,
      requested_qty: 1,
      uom: product.base_uom || 'PCS',
      priority: 'normal',
      notes: ''
    }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Update item
  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Remove item
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Save as draft
  const handleSaveDraft = async () => {
    if (items.length === 0) {
      toast({ title: 'No Items', description: 'Please add at least one item to the request', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      await stockRequestsAPI.create({
        warehouse_id: warehouseId,
        warehouse_name: warehouseName,
        items: items,
        notes: notes,
        required_by_date: requiredByDate || null
      });
      toast({ title: 'Success', description: 'Stock request saved as draft' });
      navigate('/warehouse/stock-requests');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save request', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Save and submit
  const handleSubmit = async () => {
    if (items.length === 0) {
      toast({ title: 'No Items', description: 'Please add at least one item to the request', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const created = await stockRequestsAPI.create({
        warehouse_id: warehouseId,
        warehouse_name: warehouseName,
        items: items,
        notes: notes,
        required_by_date: requiredByDate || null
      });
      
      // Submit the request
      await stockRequestsAPI.submit(created.id);
      toast({ title: 'Success', description: 'Stock request submitted to HQ for review' });
      navigate('/warehouse/stock-requests');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit request', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="stock-request-create">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/warehouse/stock-requests')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Raise Stock Request</h1>
            <p className="text-sm text-gray-500">Request stock items from HQ</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving || items.length === 0}>
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          <Button onClick={handleSubmit} disabled={saving || items.length === 0} className="bg-green-600 hover:bg-green-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Submit to HQ
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Product Search and Add */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Add Products</CardTitle>
            <CardDescription>Search and add products to request</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, SKU..."
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
            
            {searchLoading && (
              <div className="text-center py-4 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
              </div>
            )}
            
            {searchResults.length > 0 && (
              <div className="border rounded-lg max-h-96 overflow-y-auto">
                {searchResults.map(product => (
                  <button
                    key={product.id}
                    data-testid={`product-option-${product.id}`}
                    onClick={() => addProduct(product)}
                    disabled={items.some(i => i.product_id === product.id)}
                    className={`w-full text-left p-3 border-b last:border-b-0 hover:bg-gray-50 ${
                      items.some(i => i.product_id === product.id) ? 'bg-gray-100 opacity-50' : ''
                    }`}
                  >
                    <p className="font-medium text-sm"><HighlightText text={product.name} query={searchQuery} /></p>
                    <p className="text-xs text-gray-500"><HighlightText text={product.sku} query={searchQuery} /> • {product.base_uom || 'PCS'}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Request Details */}
            <div className="pt-4 border-t space-y-3">
              <div>
                <Label>Required By Date (Optional)</Label>
                <Input
                  type="date"
                  value={requiredByDate}
                  onChange={(e) => setRequiredByDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes for HQ..."
                  rows={3}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right: Request Items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Request Items</span>
              <Badge variant="secondary">{items.length} items</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No items added yet</p>
                <p className="text-sm">Search and add products from the left panel</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 border rounded-lg bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{item.product_name}</p>
                        <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <p className="text-sm text-gray-500">{item.product_sku}</p>
                      
                      <div className="grid grid-cols-3 gap-3 pt-2">
                        <div>
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            value={item.requested_qty}
                            onChange={(e) => updateItem(index, 'requested_qty', parseFloat(e.target.value) || 0)}
                            min={1}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">UOM</Label>
                          <Input value={item.uom} disabled className="h-8 bg-gray-100" />
                        </div>
                        <div>
                          <Label className="text-xs">Priority</Label>
                          <Select value={item.priority} onValueChange={(v) => updateItem(index, 'priority', v)}>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Item Notes</Label>
                        <Input
                          value={item.notes || ''}
                          onChange={(e) => updateItem(index, 'notes', e.target.value)}
                          placeholder="Optional notes..."
                          className="h-8"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StockRequestCreatePage;
